import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { selectBillData, selectLoading, selectFromDate, selectToDate, selectSelectedBranch, selectSelectedCustomer, fetchCreditBillsData } from '../../store/slices/creditReportsSlice';
import { cancelBill, updateBillPaymentMode, getReceiptDetails, settleCreditBill } from '../../utilities/api';
import { exportStyledExcel, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

const normalizeMop = (m) =>
    String(m ?? '')
        .trim()
        .toUpperCase();

const isCancelledStatus = (row) => (row.status || '').toLowerCase() === 'cancelled';

const isCreditMop = (row) => normalizeMop(row.final_mop) === 'CREDIT';

/** Same mode of payment across all rows (for cancel & change MOP). */
const validateUniformMop = (rows) => {
    if (!rows.length) return { ok: false, message: 'Select at least one bill.' };
    const first = normalizeMop(rows[0].final_mop);
    if (!first) return { ok: false, message: 'Selected bills are missing mode of payment.' };
    const mismatch = rows.find((r) => normalizeMop(r.final_mop) !== first);
    if (mismatch) {
        return {
            ok: false,
            message: 'All selected bills must have the same mode of payment for this action.'
        };
    }
    return { ok: true, mop: rows[0].final_mop };
};

const validateSettlementSelection = (rows) => {
    if (!rows.length) return { ok: false, message: 'Select at least one bill.' };
    const nonCredit = rows.filter((r) => !isCreditMop(r));
    if (nonCredit.length) {
        return {
            ok: false,
            message: 'Settlement is only allowed for bills with mode of payment Credit.'
        };
    }
    const cancelled = rows.filter(isCancelledStatus);
    if (cancelled.length) {
        return { ok: false, message: 'Remove cancelled bills from the selection.' };
    }
    return { ok: true };
};

const validateCancelOrChangeMop = (rows) => {
    const base = validateUniformMop(rows);
    if (!base.ok) return base;
    const cancelled = rows.filter(isCancelledStatus);
    if (cancelled.length) {
        return { ok: false, message: 'Cancelled bills cannot be included for this action.' };
    }
    return { ok: true, mop: base.mop };
};

/** Some legacy rows store branch_id as a bogus "0" on the per-branch table; treat that as unusable. */
const isUsableBranchId = (id) => {
    const s = String(id ?? '').trim();
    return s !== '' && s !== '0';
};

const getBranchIdForRow = (row, selectedBranch) => {
    if (isUsableBranchId(row.branch_id)) return String(row.branch_id).trim();
    if (isUsableBranchId(selectedBranch?.branch_id)) return String(selectedBranch.branch_id).trim();
    const stored = localStorage.getItem('branchid');
    return isUsableBranchId(stored) ? String(stored).trim() : '';
};

const validateSameBranch = (rows, selectedBranch) => {
    const ids = rows.map((r) => getBranchIdForRow(r, selectedBranch)).filter(Boolean);
    if (!ids.length) {
        return { ok: false, message: 'Could not resolve branch for selected bill(s). Select a branch in the filter or ensure bills include branch information.' };
    }
    const first = String(ids[0]);
    if (ids.some((id) => String(id) !== first)) {
        return {
            ok: false,
            message: 'For this action, select bills from the same branch only (payment options are loaded per branch).'
        };
    }
    return { ok: true, branchId: first };
};

/** Branch must be chosen in the report header dropdown (not cleared, not "All"). */
const isBranchChosenInFilter = (selectedBranch) => {
    if (selectedBranch == null) return false;
    const id = selectedBranch.branch_id;
    if (id === undefined || id === null) return false;
    const s = String(id).trim();
    if (!s) return false;
    if (s.toLowerCase() === 'all') return false;
    return true;
};

const getStoredBranchId = () => {
    const id = localStorage.getItem('branchid');
    if (id === undefined || id === null) return '';
    const s = String(id).trim();
    if (!s || s.toLowerCase() === 'all') return '';
    return s;
};

/** Filter branch, or localStorage branch for manager/admin when filter is unset or "All". */
const resolveActionBranchId = (selectedBranch, { isManager, isAdmin } = {}) => {
    if (isBranchChosenInFilter(selectedBranch)) {
        return String(selectedBranch.branch_id).trim();
    }
    if (isManager || isAdmin) {
        return getStoredBranchId();
    }
    return '';
};

const parseReceiptPayload = (data) => {
    let d = data;
    if (typeof d === 'string') {
        try {
            d = JSON.parse(d);
        } catch {
            return null;
        }
    }
    if (d?.final_output) {
        try {
            d = JSON.parse(d.final_output);
        } catch {
            return null;
        }
    }
    return d;
};

/** Payment modes from receipt_settings excluding current (for change MOP). */
const getAlternatePaymentOptions = (receiptSettings, currentPaymentMode) => {
    if (!receiptSettings || !Array.isArray(receiptSettings)) return [];
    const current = normalizeMop(currentPaymentMode);
    return receiptSettings
        .filter((s) => (s.setting_type === 'Payment' || (s.setting_type && String(s.setting_type).toLowerCase() === 'payment')) && String(s.setting_access) === '1')
        .filter((s) => {
            const name = normalizeMop(s.setting_name || s.payment_name || s.setting_value);
            return name && name !== current;
        })
        .map((s) => ({
            label: s.setting_name || s.payment_name || s.setting_value || 'Payment',
            value: s.setting_name || s.payment_name || s.setting_value
        }));
};

/** Payment modes for settlement (non-credit). */
const getSettlementPaymentOptions = (receiptSettings) => {
    if (!receiptSettings || !Array.isArray(receiptSettings)) return [];
    return receiptSettings
        .filter((s) => (s.setting_type === 'Payment' || (s.setting_type && String(s.setting_type).toLowerCase() === 'payment')) && String(s.setting_access) === '1')
        .filter((s) => {
            const name = normalizeMop(s.setting_name || s.payment_name || s.setting_value);
            return name && name !== 'CREDIT' && !name.includes('CREDIT');
        })
        .map((s) => ({
            label: s.setting_name || s.payment_name || s.setting_value || 'Payment',
            value: s.setting_name || s.payment_name || s.setting_value
        }));
};

const isSettleSuccess = (data) => {
    const row = Array.isArray(data) ? data[0] : data;
    return String(row?.status || '').toLowerCase() === 'success';
};

const cancelReasonOptions = [
    { label: 'Duplicate Item', value: 'duplicate item' },
    { label: 'Wrong Item Name', value: 'wrong item name' },
    { label: 'Item Not Available', value: 'item not available' }
];

const CreditReportsContent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useRef(null);

    const billData = useSelector(selectBillData);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedCustomer = useSelector(selectSelectedCustomer);

    const rolename = localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const isManager = rolename.toUpperCase() === 'MANAGER';
    const canExport = isCEO || isAdmin;
    const editedBy = localStorage.getItem('userid') || '';

    const [filteredBillData, setFilteredBillData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);

    const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
    const [cancelRemarks, setCancelRemarks] = useState('');
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    const [changeMopDialogVisible, setChangeMopDialogVisible] = useState(false);
    const [changeMopOptions, setChangeMopOptions] = useState([]);
    const [selectedNewMop, setSelectedNewMop] = useState(null);
    const [changeMopRemarks, setChangeMopRemarks] = useState('');
    const [changeMopLoading, setChangeMopLoading] = useState(false);
    const [changeMopSubmitting, setChangeMopSubmitting] = useState(false);

    const [settlementDialogVisible, setSettlementDialogVisible] = useState(false);
    const [settlementPaymentOptions, setSettlementPaymentOptions] = useState([]);
    const [settlementPaymentType, setSettlementPaymentType] = useState(null);
    const [settlementDate, setSettlementDate] = useState(null);
    const [settlementRemarks, setSettlementRemarks] = useState('');
    const [settlementLoading, setSettlementLoading] = useState(false);
    const [settlementSubmitting, setSettlementSubmitting] = useState(false);

    useEffect(() => {
        setFilteredBillData(billData);
        setGlobalFilter('');
        setSelectedRows([]);
    }, [billData]);

    const tableRows = useMemo(
        () =>
            filteredBillData.map((row) => ({
                ...row,
                __selectionKey: `${String(row.bill_id)}__${String(getBranchIdForRow(row, selectedBranch))}`
            })),
        [filteredBillData, selectedBranch]
    );

    useEffect(() => {
        setSelectedRows((prev) => {
            if (!prev.length) return prev;
            const byKey = new Map(tableRows.map((r) => [r.__selectionKey, r]));
            const next = prev.map((r) => byKey.get(r.__selectionKey)).filter(Boolean);
            return next;
        });
    }, [tableRows]);

    const refetchBills = useCallback(() => {
        const to = toDate ? new Date(toDate) : new Date();
        const from = fromDate
            ? new Date(fromDate)
            : (() => {
                  const d = new Date();
                  if (!isManager) {
                      d.setDate(d.getDate() - 30);
                  }
                  return d;
              })();
        const branchId = resolveActionBranchId(selectedBranch, { isManager, isAdmin }) || selectedBranch?.branch_id || '';
        dispatch(
            fetchCreditBillsData({
                fromDate: from,
                toDate: to,
                branchId,
                custName: selectedCustomer || ''
            })
        );
    }, [dispatch, fromDate, toDate, selectedBranch, selectedCustomer, isManager, isAdmin]);

    const formatDate = (dateString) => {
        return moment(dateString).isValid() ? moment(dateString).format('DD-MM-YYYY') : '';
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const viewBill = (rowData) => {
        if (rowData.bill_id) {
            const branchId = getBranchIdForRow(rowData, selectedBranch);

            if (!branchId) {
                alert('Please select a branch in the filter to view the receipt.');
                return;
            }
            navigate(`/billreceipt/${rowData.bill_id}/${branchId}`, {
                state: { userId: rowData.userId ?? rowData.user_id }
            });
        }
    };

    const actionBodyTemplate = (rowData) => {
        return <Button label="View" icon="pi pi-eye" className="p-button-sm p-button-info" onClick={() => viewBill(rowData)} />;
    };

    const openCancelDialog = () => {
        const v = validateCancelOrChangeMop(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Cannot cancel', detail: v.message, life: 5000 });
            return;
        }
        setCancelRemarks('');
        setCancelDialogVisible(true);
    };

    const submitCancelBills = async () => {
        if (!cancelRemarks) {
            toast.current?.show({ severity: 'warn', summary: 'Reason required', detail: 'Please select a reason for cancellation.', life: 4000 });
            return;
        }
        if (!editedBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setCancelSubmitting(true);
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) {
                    throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                }
                await cancelBill(row.bill_id, branchId, cancelRemarks, editedBy);
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Cancelled',
                detail: `${selectedRows.length} bill(s) cancelled successfully.`,
                life: 4000
            });
            setCancelDialogVisible(false);
            setCancelRemarks('');
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Cancel failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to cancel bill(s)',
                life: 5000
            });
        } finally {
            setCancelSubmitting(false);
        }
    };

    const openChangeMopDialog = async () => {
        const v = validateCancelOrChangeMop(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Cannot change payment', detail: v.message, life: 5000 });
            return;
        }
        const branchCheck = validateSameBranch(selectedRows, selectedBranch);
        if (!branchCheck.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Branch', detail: branchCheck.message, life: 5000 });
            return;
        }
        const first = selectedRows[0];
        const branchId = branchCheck.branchId;
        setSelectedNewMop(null);
        setChangeMopRemarks('');
        setChangeMopOptions([]);
        setChangeMopDialogVisible(true);
        setChangeMopLoading(true);
        try {
            let data = await getReceiptDetails(first.bill_id, branchId);
            const parsed = parseReceiptPayload(data);
            const settings = parsed?.receipt_settings || [];
            const options = getAlternatePaymentOptions(settings, first.final_mop);
            setChangeMopOptions(options);
            if (!options.length) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'No other payment modes',
                    detail: 'No alternate payment modes are available from receipt settings.',
                    life: 5000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Load failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to load payment modes',
                life: 5000
            });
            setChangeMopDialogVisible(false);
        } finally {
            setChangeMopLoading(false);
        }
    };

    const submitChangeMop = async () => {
        if (!selectedNewMop) {
            toast.current?.show({ severity: 'warn', summary: 'Select payment', detail: 'Please select a new mode of payment.', life: 4000 });
            return;
        }
        if (!changeMopRemarks.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Remarks required', detail: 'Please enter remarks for this payment mode change.', life: 4000 });
            return;
        }
        if (!editedBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setChangeMopSubmitting(true);
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                await updateBillPaymentMode(row.bill_id, branchId, selectedNewMop, editedBy, changeMopRemarks.trim());
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Updated',
                detail: `Payment mode updated for ${selectedRows.length} bill(s).`,
                life: 4000
            });
            setChangeMopDialogVisible(false);
            setSelectedNewMop(null);
            setChangeMopRemarks('');
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Update failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to update payment mode',
                life: 5000
            });
        } finally {
            setChangeMopSubmitting(false);
        }
    };

    const openSettlementDialog = async () => {
        const v = validateSettlementSelection(selectedRows);
        if (!v.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Settlement', detail: v.message, life: 5000 });
            return;
        }
        const branchCheck = validateSameBranch(selectedRows, selectedBranch);
        if (!branchCheck.ok) {
            toast.current?.show({ severity: 'warn', summary: 'Branch', detail: branchCheck.message, life: 5000 });
            return;
        }
        const first = selectedRows[0];
        const branchId = branchCheck.branchId;
        setSettlementPaymentType(null);
        setSettlementDate(null);
        setSettlementRemarks('');
        setSettlementPaymentOptions([]);
        setSettlementDialogVisible(true);
        setSettlementLoading(true);
        try {
            let data = await getReceiptDetails(first.bill_id, branchId);
            const parsed = parseReceiptPayload(data);
            const settings = parsed?.receipt_settings || [];
            const options = getSettlementPaymentOptions(settings);
            setSettlementPaymentOptions(options);
            if (!options.length) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'No payment modes',
                    detail: 'No non-credit payment modes found in receipt settings.',
                    life: 5000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Load failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to load settlement options',
                life: 5000
            });
            setSettlementDialogVisible(false);
        } finally {
            setSettlementLoading(false);
        }
    };

    const submitSettlement = async () => {
        if (!settlementPaymentType) {
            toast.current?.show({ severity: 'warn', summary: 'Payment type', detail: 'Please select a payment type.', life: 4000 });
            return;
        }
        if (!settlementDate) {
            toast.current?.show({ severity: 'warn', summary: 'Date', detail: 'Please select settlement date.', life: 4000 });
            return;
        }
        if (!settlementRemarks.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Remarks required', detail: 'Please enter remarks for this settlement.', life: 4000 });
            return;
        }
        const settlementBy = editedBy;
        if (!settlementBy) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'User id not found. Please log in again.', life: 4000 });
            return;
        }
        setSettlementSubmitting(true);
        const dateStr = moment(settlementDate).format('YYYY-MM-DD');
        try {
            for (const row of selectedRows) {
                const branchId = getBranchIdForRow(row, selectedBranch);
                if (!branchId) throw new Error(`Branch is missing for bill ${row.bill_id}.`);
                const amount = String(row.total_paid_amount ?? row.balance ?? '');
                const payload = {
                    bill_id: row.bill_id,
                    branch_id: branchId,
                    payment_type: settlementPaymentType,
                    amount,
                    remarks: settlementRemarks.trim(),
                    settlement_date: dateStr,
                    settlement_by: settlementBy
                };
                const res = await settleCreditBill(payload);
                if (!isSettleSuccess(res)) {
                    throw new Error(typeof res === 'object' ? JSON.stringify(res) : 'Settlement was not successful');
                }
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Settled',
                detail: `${selectedRows.length} bill(s) settled successfully.`,
                life: 4000
            });
            setSettlementDialogVisible(false);
            setSelectedRows([]);
            refetchBills();
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Settlement failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to settle bill(s)',
                life: 5000
            });
        } finally {
            setSettlementSubmitting(false);
        }
    };

    const exportToPDF = () => {
        if (filteredBillData.length === 0) {
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Credit Report', 14, 15);

        doc.setFontSize(10);
        let yPos = 25;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
            yPos += 5;
        }
        if (selectedBranch) {
            doc.text(`Branch: ${selectedBranch.branch_name}`, 14, yPos);
            yPos += 5;
        }
        if (selectedCustomer) {
            doc.text(`Customer: ${selectedCustomer}`, 14, yPos);
            yPos += 5;
        }

        const tableBody = filteredBillData.map((b) => [
            b.bill_id || '',
            b.invoice_no || '',
            formatDate(b.invoice_date),
            b.cust_name || '',
            formatCurrency(b.total_paid_amount),
            b.final_mop || '',
            b.status || ''
        ]);

        if (isCEO) {
            const grandTotal = filteredBillData.reduce((acc, curr) => acc + parseFloat(curr.total_paid_amount || 0), 0);
            tableBody.push(['', '', '', '', formatCurrency(grandTotal), '', '']);
        }

        autoTable(doc, {
            startY: yPos + 5,
            head: [['Bill ID', 'Invoice No', 'Date', 'Customer', 'Total', 'MOP', 'Status']],
            body: tableBody,
            footStyles: { fontStyle: 'bold' },
            styles: { fontSize: 8 }
        });

        doc.save(`CreditReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const exportToExcel = async () => {
        if (filteredBillData.length === 0) {
            return;
        }

        const excelColumnMap = {
            invoice_no: { header: 'Bill No', getValue: (b) => b.invoice_no || '-' },
            invoice_date: { header: 'Bill Date', getValue: (b) => formatDate(b.invoice_date) },
            invoice_time: { header: 'Bill Time', getValue: (b) => b.invoice_time || '-' },
            cust_name: { header: 'Cust Name', getValue: (b) => b.cust_name || '-' },
            total_paid_amount: { header: 'Amt', getValue: (b) => parseFloat(b.total_paid_amount || 0), numeric: true }
        };

        const billColumns = [{ header: 'Sl.No', getValue: (b, index) => index + 1 }, ...visibleBillColumns.map((col) => excelColumnMap[col.field]).filter(Boolean)];

        const filterLines = buildReportFilterLines({ fromDate, toDate, selectedBranch });
        if (selectedCustomer) filterLines.push(`Customer: ${selectedCustomer}`);

        await exportStyledExcel({
            fileName: `CreditReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Credit Report',
            title: 'Credit Report',
            filterLines,
            columns: billColumns,
            rows: filteredBillData,
            showFooterTotal: isCEO
        });
    };

    const grandTotal = filteredBillData.reduce((sum, item) => sum + parseFloat(item.total_paid_amount || 0), 0);

    useEffect(() => {
        const searchTerm = globalFilter.trim().toLowerCase();
        if (!searchTerm) {
            setFilteredBillData(billData);
            return;
        }
        const filtered = billData.filter(
            (item) =>
                (item.bill_id && item.bill_id.toString().toLowerCase().includes(searchTerm)) ||
                (item.invoice_no && item.invoice_no.toLowerCase().includes(searchTerm)) ||
                (item.cust_name && item.cust_name.toLowerCase().includes(searchTerm)) ||
                (item.final_mop && item.final_mop.toLowerCase().includes(searchTerm)) ||
                (item.status && item.status.toLowerCase().includes(searchTerm))
        );
        setFilteredBillData(filtered);
    }, [globalFilter, billData]);

    const selectionCount = selectedRows.length;
    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const n = parseFloat(String(value).trim());
        return Number.isFinite(n) && n === 0;
    };

    const billColumns = [
        { field: 'invoice_no', header: 'Bill No', sortable: true, style: { minWidth: '140px' } },
        { field: 'invoice_date', header: 'Bill Date', sortable: true, style: { minWidth: '100px' }, body: (row) => formatDate(row.invoice_date) },
        { field: 'invoice_time', header: 'Bill Time', style: { minWidth: '90px' } },
        { field: 'cust_name', header: 'Cust Name', sortable: true, style: { minWidth: '140px' }, body: (row) => row.cust_name || '-' },
        { field: 'total_paid_amount', header: 'Amt', sortable: true, style: { textAlign: 'right', minWidth: '100px' }, body: (row) => `₹${formatCurrency(row.total_paid_amount)}`, hideIfAllZero: true }
    ];

    const visibleBillColumns = billColumns.filter((col) => {
        if (!col.hideIfAllZero) return true;
        return !filteredBillData.every((row) => isZeroLike(row?.[col.field]));
    });

    const totalColumnIndex = visibleBillColumns.findIndex((col) => col.field === 'total_paid_amount');
    const billFooterGroup =
        isCEO && totalColumnIndex !== -1 ? (
            <ColumnGroup>
                <Row>
                    <Column footer="" />
                    <Column footer="" />
                    {visibleBillColumns.map((col, index) => (
                        <Column
                            key={col.field}
                            footer={index === Math.max(0, totalColumnIndex - 1) ? 'Grand Total:' : index === totalColumnIndex ? `₹${formatCurrency(grandTotal)}` : ''}
                            style={index >= totalColumnIndex - 1 ? { textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' } : undefined}
                        />
                    ))}
                    <Column footer="" />
                </Row>
            </ColumnGroup>
        ) : null;

    const cancelDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setCancelDialogVisible(false)} disabled={cancelSubmitting} />
            <Button label="Confirm cancel" icon="pi pi-ban" className="p-button-danger" onClick={submitCancelBills} loading={cancelSubmitting} />
        </>
    );

    const changeMopDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setChangeMopDialogVisible(false)} disabled={changeMopSubmitting} />
            <Button
                label="Update payment mode"
                icon="pi pi-check"
                onClick={submitChangeMop}
                loading={changeMopSubmitting}
                disabled={changeMopSubmitting || changeMopLoading || !selectedNewMop || !changeMopRemarks.trim()}
            />
        </>
    );

    const settlementDialogFooter = (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setSettlementDialogVisible(false)} disabled={settlementSubmitting} />
            <Button
                label="Settle bills"
                icon="pi pi-check"
                onClick={submitSettlement}
                loading={settlementSubmitting}
                disabled={settlementSubmitting || settlementLoading || !settlementPaymentType || !settlementDate || !settlementRemarks.trim()}
            />
        </>
    );

    return (
        <div className="bill-reports-container">
            <Toast ref={toast} />

            <Dialog header="Cancel bill(s)" visible={cancelDialogVisible} style={{ width: 'min(480px, 95vw)' }} onHide={() => !cancelSubmitting && setCancelDialogVisible(false)} footer={cancelDialogFooter}>
                <p className="mt-0 text-color-secondary">
                    {selectionCount} bill(s) selected. Same mode of payment: <strong>{selectedRows[0]?.final_mop || '—'}</strong>
                </p>
                <label className="font-semibold block mb-2">
                    Select reason <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-column gap-2">
                    {cancelReasonOptions.map((option) => (
                        <div key={option.value} className="flex align-items-center gap-2">
                            <RadioButton inputId={`cancel-reason-${option.value}`} name="cancelReason" value={option.value} onChange={(e) => setCancelRemarks(e.value)} checked={cancelRemarks === option.value} />
                            <label htmlFor={`cancel-reason-${option.value}`}>{option.label}</label>
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog header="Change mode of payment" visible={changeMopDialogVisible} style={{ width: 'min(480px, 95vw)' }} onHide={() => !changeMopSubmitting && setChangeMopDialogVisible(false)} footer={changeMopDialogFooter}>
                {changeMopLoading ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: 40, height: 40 }} />
                    </div>
                ) : (
                    <>
                        <p className="mt-0 text-color-secondary">
                            {selectionCount} bill(s). Current mode: <strong>{selectedRows[0]?.final_mop || '—'}</strong>. Choose a new mode (excluding the current one):
                        </p>
                        <Dropdown value={selectedNewMop} options={changeMopOptions} onChange={(e) => setSelectedNewMop(e.value)} placeholder="Select new payment mode" className="w-full" optionLabel="label" optionValue="value" />
                        {selectedNewMop && (
                            <div className="mt-3">
                                <label className="font-semibold block mb-2">Remarks</label>
                                <InputTextarea value={changeMopRemarks} onChange={(e) => setChangeMopRemarks(e.target.value)} rows={2} className="w-full" placeholder="Reason for changing the payment mode" />
                            </div>
                        )}
                    </>
                )}
            </Dialog>

            <Dialog header="Settlement (credit bills)" visible={settlementDialogVisible} style={{ width: 'min(520px, 95vw)' }} onHide={() => !settlementSubmitting && setSettlementDialogVisible(false)} footer={settlementDialogFooter}>
                {settlementLoading ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: 40, height: 40 }} />
                    </div>
                ) : (
                    <>
                        <p className="mt-0 text-color-secondary">
                            {selectionCount} credit bill(s). Each will be settled for its <strong>total paid amount</strong>.
                        </p>
                        <ul className="text-sm pl-3 mb-3" style={{ maxHeight: 120, overflow: 'auto' }}>
                            {selectedRows.map((r) => (
                                <li key={r.__selectionKey}>
                                    Bill {r.bill_id}: ₹{formatCurrency(r.total_paid_amount)}
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-column gap-3">
                            <div>
                                <label className="font-semibold block mb-2">Payment type</label>
                                <Dropdown
                                    value={settlementPaymentType}
                                    options={settlementPaymentOptions}
                                    onChange={(e) => setSettlementPaymentType(e.value)}
                                    placeholder="Select payment type"
                                    className="w-full"
                                    optionLabel="label"
                                    optionValue="value"
                                />
                            </div>
                            <div>
                                <label className="font-semibold block mb-2">Settlement date</label>
                                <Calendar value={settlementDate} onChange={(e) => setSettlementDate(e.value)} dateFormat="dd-mm-yy" showIcon className="w-full" showButtonBar />
                            </div>
                            <div>
                                <label className="font-semibold block mb-2">Remarks</label>
                                <InputTextarea value={settlementRemarks} onChange={(e) => setSettlementRemarks(e.target.value)} rows={2} className="w-full" placeholder="Reason for this settlement" />
                            </div>
                        </div>
                    </>
                )}
            </Dialog>

            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="flex align-items-center gap-2">
                        <h3 className="text-lg font-semibold m-0">Report Data</h3>
                        {filteredBillData.length > 0 && <Tag value={`${filteredBillData.length} Records`} severity="info" className="ml-2" />}
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search bills..." className="w-full" />
                        </span>

                        {canExport && (
                            <>
                                <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportToExcel} disabled={filteredBillData.length === 0} />
                                <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportToPDF} disabled={filteredBillData.length === 0} />
                            </>
                        )}
                    </div>
                </div>

                {selectionCount > 0 && (
                    <div className="bill-bulk-actions flex flex-wrap align-items-center gap-2 mb-3 p-3 border-round surface-border border-1 surface-50">
                        <span className="font-semibold mr-2">{selectionCount} selected</span>
                        <Button label="Cancel bill" icon="pi pi-times-circle" className="p-button-danger p-button-sm" onClick={openCancelDialog} />
                        <Button label="Change mode of payment" icon="pi pi-wallet" className="p-button-secondary p-button-sm" onClick={openChangeMopDialog} />
                        <Button label="Settlement" icon="pi pi-money-bill" className="p-button-sm" onClick={openSettlementDialog} />
                        <Button label="Clear selection" icon="pi pi-times" className="p-button-text p-button-sm ml-auto" onClick={() => setSelectedRows([])} />
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : filteredBillData.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6">
                        <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                        <p className="text-xl text-color-secondary">No data available</p>
                        <p className="text-color-secondary mt-2">{billData.length === 0 ? 'Please apply filters to view credit bills' : 'No records match your search criteria'}</p>
                    </div>
                ) : (
                    <DataTable
                        value={tableRows}
                        selection={selectedRows}
                        onSelectionChange={(e) => setSelectedRows(e.value)}
                        dataKey="__selectionKey"
                        showGridlines
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        tableStyle={{ minWidth: '50rem' }}
                        className="p-datatable-sm"
                        emptyMessage="No credit bills found"
                        footerColumnGroup={billFooterGroup}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                        <Column header="Sl.No" body={(row, options) => options.rowIndex + 1} style={{ minWidth: '60px' }} />
                        {visibleBillColumns.map((col) => (
                            <Column key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable} style={col.style} />
                        ))}
                        <Column header="Actions" body={actionBodyTemplate} style={{ textAlign: 'center', minWidth: '80px' }} />
                    </DataTable>
                )}
            </Card>
        </div>
    );
};

export default CreditReportsContent;
